import { TrashIcon, UploadIcon } from '@primer/octicons-react';
import { Box, Button } from '@primer/react';
import { useState } from 'react';
import { NotifyHelper } from '../../helpers/toast';
import { apiClient } from '../../api/apiClient';

interface MultiImageUploadProps {
  value?: string[];
  onValueChanged: (images: string[]) => void;
  maxImages?: number;
  lichSuYTeId?: number; // ID của lịch sử y tế để upload
}

const MultiImageUpload: React.FC<MultiImageUploadProps> = ({
  value = [],
  onValueChanged,
  maxImages = 10,
  lichSuYTeId,
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });

      // Sử dụng API khác nhau tùy theo có ID hay không
      const uploadPath = lichSuYTeId
        ? `lich-su-xuong-phong-y-te/upload/lich-su-y-te/${lichSuYTeId}`
        : 'lich-su-xuong-phong-y-te/upload/lich-su-y-te-temp';

      const response = await apiClient.upload(uploadPath, formData);

      if (response.is_success) {
        const result = response.data;
        if (result.status === 'success') {
          const newImages = [...value, ...result.images];
          onValueChanged(newImages);
          NotifyHelper.Success(`Đã upload ${result.count} ảnh thành công`);
        } else {
          NotifyHelper.Error(result.message || 'Upload ảnh thất bại');
        }
      } else {
        NotifyHelper.Error(response.message || 'Upload ảnh thất bại');
      }
    } catch (error) {
      console.error('Upload error:', error);
      NotifyHelper.Error('Có lỗi xảy ra khi upload ảnh');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = async (index: number) => {
    const imageUrl = value[index];
    if (!lichSuYTeId) {
      // Nếu chưa có ID, chỉ xóa khỏi state
      const newImages = value.filter((_, i) => i !== index);
      onValueChanged(newImages);
      return;
    }

    try {
      const response = await apiClient.delete(`lich-su-xuong-phong-y-te/delete-image/lich-su-y-te/${lichSuYTeId}/${encodeURIComponent(imageUrl)}`);

      if (response.is_success) {
        const newImages = value.filter((_, i) => i !== index);
        onValueChanged(newImages);
        NotifyHelper.Success('Đã xóa ảnh thành công');
      } else {
        NotifyHelper.Error(response.message || 'Xóa ảnh thất bại');
      }
    } catch (error) {
      console.error('Delete error:', error);
      NotifyHelper.Error('Có lỗi xảy ra khi xóa ảnh');
    }
  };

  const canUploadMore = value.length < maxImages;

  return (
    <Box>
      {/* Hiển thị danh sách ảnh đã upload */}
      {value.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 2, fontSize: 1, color: 'fg.muted' }}>
            Đã upload {value.length} ảnh (tối đa {maxImages} ảnh)
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
            gap: 1,
            maxHeight: '200px',
            overflowY: 'auto',
            p: 2,
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: 2,
            bg: 'canvas.subtle'
          }}>
            {value.map((imageUrl, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  border: '1px solid',
                  borderColor: 'border.default',
                  borderRadius: 1,
                  overflow: 'hidden',
                  aspectRatio: '1',
                }}
              >
                <img
                  src={imageUrl}
                  alt={`Image ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Button
                  variant="danger"
                  size="small"
                  leadingVisual={TrashIcon}
                  onClick={() => handleRemoveImage(index)}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    minWidth: '20px',
                    minHeight: '20px',
                    p: 0,
                    fontSize: '10px',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Upload area */}
      {canUploadMore && (
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'border.default',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            minHeight: '80px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'accent.emphasis',
              bg: 'canvas.subtle',
            },
          }}
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.accept = 'image/*';
            input.onchange = (e) => {
              const target = e.target as HTMLInputElement;
              handleFileUpload(target.files);
            };
            input.click();
          }}
        >
          <UploadIcon size={20} />
          <Box sx={{ mt: 1, fontSize: 1 }}>
            {isUploading ? 'Đang upload...' : 'Click để chọn ảnh'}
          </Box>
        </Box>
      )}

      {!canUploadMore && (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: 2,
            bg: 'canvas.subtle',
            color: 'fg.muted',
          }}
        >
          Đã đạt giới hạn tối đa {maxImages} ảnh
        </Box>
      )}

      {value.length > 0 && (
        <Box sx={{ mt: 2, fontSize: 1, color: 'fg.muted' }}>
          {value.length}/{maxImages} ảnh
        </Box>
      )}
    </Box>
  );
};

export default MultiImageUpload;
